import { io, type Socket } from 'socket.io-client';
import { getAccessToken, isTokenExpired } from './token-manager';
import { refreshAccessToken } from './api-interceptors';

/**
 * One Socket.IO connection per tab for live "something changed" signals
 * (messaging). The server only pushes ids; callers refetch through the normal
 * API. Pages keep a slow poll as a fallback, so a dead socket only delays updates.
 */

const API_BASE: string = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/** An absolute API URL means the API is on another origin; a relative one shares ours. */
function socketOrigin(): string | undefined {
  try {
    return new URL(API_BASE).origin;
  } catch {
    return undefined;
  }
}

let socket: Socket | null = null;
let connected = false;
let authRetries = 0;
const listeners = new Set<() => void>();

function setConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  listeners.forEach((notify) => notify());
}

/** Reconnect with a freshly refreshed token; gives up after a couple of tries
 *  (the HTTP layer handles a truly dead session by sending people to login). */
function reconnectWithFreshToken(s: Socket) {
  if (authRetries >= 2) return;
  authRetries++;
  refreshAccessToken(API_BASE)
    .then(() => s.connect())
    .catch(() => undefined);
}

export function connectRealtime(): Socket {
  if (socket) {
    if (!socket.connected && !socket.active) socket.connect();
    return socket;
  }
  const options = {
    path: '/socket.io',
    withCredentials: true,
    // Read the token at every (re)connect so it is never stale.
    auth: (cb: (data: { token: string | null }) => void) => {
      if (isTokenExpired(30)) {
        refreshAccessToken(API_BASE)
          .catch(() => undefined)
          .finally(() => cb({ token: getAccessToken() }));
      } else {
        cb({ token: getAccessToken() });
      }
    },
  };
  const origin = socketOrigin();
  const s = origin ? io(origin, options) : io(options);

  s.on('connect', () => {
    authRetries = 0;
    setConnected(true);
  });
  s.on('disconnect', (reason) => {
    setConnected(false);
    // The server closes the socket when the access token expires.
    if (reason === 'io server disconnect') reconnectWithFreshToken(s);
  });
  s.on('connect_error', (err) => {
    setConnected(false);
    // Auth failures aren't retried by the client on its own.
    if (err.message === 'unauthorized') reconnectWithFreshToken(s);
  });

  socket = s;
  return s;
}

let lastTypingSent = new Map<string, number>();

/** Tells the other people in a thread you're typing (throttled; the server checks membership). */
export function emitTyping(conversationId: string): void {
  if (!socket?.connected) return;
  const now = Date.now();
  if (now - (lastTypingSent.get(conversationId) ?? 0) < 2500) return;
  lastTypingSent.set(conversationId, now);
  socket.emit('typing', { conversationId });
}

export interface PresenceUpdate {
  userId: string;
  online: boolean;
  lastSeenAt: string | null;
}

let watched: string[] = [];

/**
 * Follow these people's online status (replaces the previous set). Resolves
 * with the current state of those the server lets you see; later changes
 * arrive as 'presence' events. Re-sent automatically after a reconnect.
 */
export function watchPresence(userIds: string[]): Promise<PresenceUpdate[]> {
  watched = userIds;
  if (!socket?.connected) return Promise.resolve([]);
  return socket
    .timeout(5000)
    .emitWithAck('presence:watch', { userIds })
    .then((res: { presence?: PresenceUpdate[] }) => res?.presence ?? [])
    .catch(() => []);
}

export function watchedPresence(): string[] {
  return watched;
}

export function disconnectRealtime(): void {
  lastTypingSent = new Map();
  watched = [];
  socket?.disconnect();
  socket = null;
  setConnected(false);
}

export function subscribeRealtimeStatus(notify: () => void): () => void {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

export function isRealtimeConnected(): boolean {
  return connected;
}
